/**
 * Collapse state for rule-based path group cards (left column).
 * Chevron toggles independently; selecting a header/path expands that card.
 */

import { useCallback, useEffect, useState } from 'react';

export function useRuleGroupsCollapse(groupCount: number) {
  const [collapsed, setCollapsed] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    setCollapsed((prev) => {
      const next = new Set<number>();
      prev.forEach((i) => {
        if (i < groupCount) next.add(i);
      });
      return next;
    });
  }, [groupCount]);

  const expandCard = useCallback((idx: number) => {
    setCollapsed((prev) => {
      if (!prev.has(idx)) return prev;
      const next = new Set(prev);
      next.delete(idx);
      return next;
    });
  }, []);

  const toggleCardCollapsed = useCallback((idx: number) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }, []);

  const toggleExpandCollapseAll = useCallback((total: number) => {
    if (total === 0) return;
    setCollapsed((prev) =>
      prev.size === 0
        ? new Set(Array.from({ length: total }, (_, i) => i))
        : new Set(),
    );
  }, []);

  const isCardExpanded = useCallback(
    (idx: number) => !collapsed.has(idx),
    [collapsed],
  );

  return {
    collapsed,
    expandCard,
    toggleCardCollapsed,
    toggleExpandCollapseAll,
    isCardExpanded,
    allExpanded: collapsed.size === 0,
  };
}
